import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter } from "react-router-dom";
import { Provider } from 'react-redux';
import {store, persistor}  from './redux/app/store';
import './assets/styles/_global.scss';
import { ApolloClient, ApolloProvider, HttpLink, InMemoryCache, from } from '@apollo/client'
import {setContext} from '@apollo/client/link/context'
import { onError } from "@apollo/client/link/error";
import {config} from './config/config'
import {Amplify} from 'aws-amplify'
import {fetchAuthSession, getCurrentUser} from 'aws-amplify/auth'
import { GlobalProvider } from './MeetContext/MeetContext';
import { MeetingProvider } from 'amazon-chime-sdk-component-library-react';
import { ChimeMeetingProvider } from './Chime/ChimeMeetingContext';
import { logout as authLogout } from './redux/slices/authSlice';
import { PersistGate } from 'redux-persist/integration/react';
import { validateJwtToken } from './utils/helper';
import ToasterWithMessage from './components/toaster';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { FETCH_POLICY } from './utils/constants';

const { error: errorToast } = ToasterWithMessage();

Amplify.configure(config.amplifyConfig);

//get new accessToken
const getNewAccessToken = async () => {
  try {
    const session = await fetchAuthSession({ forceRefresh: true });
    if (!session?.tokens?.accessToken) {
      store.dispatch(authLogout()); 
      throw new Error("Refresh token expired or invalid session");
    }
    return session;
  } catch (error: any) {
    console.error("[Log] Token refresh failed:", error);
    throw error; 
  }
};

// Manually refresh Cognito tokens using refresh token
const refreshCognitoTokens = async (refreshToken: string) => {
  try {
    const region = config.amplifyConfig.aws_cognito_region || "";
    const clientId = config.amplifyConfig.aws_user_pools_web_client_id || "";
    const url = `https://cognito-idp.${region}.amazonaws.com/`;

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-amz-json-1.1",
        "X-Amz-Target": "AWSCognitoIdentityProviderService.InitiateAuth",
      },
      body: JSON.stringify({
        ClientId: clientId,
        AuthFlow: "REFRESH_TOKEN_AUTH",
        AuthParameters: {
          REFRESH_TOKEN: refreshToken,
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to refresh Cognito tokens: ${response.statusText}`);
    }

    const data = await response.json();
    return {
      AccessToken: data.AuthenticationResult.AccessToken,
      IdToken: data.AuthenticationResult.IdToken,
    };
  } catch (error) {
    console.error("[Log] Temp token refresh failed:", error);
    throw error;
  }
};

//checking that the accessToken is Expired or not`
const setAuthorizationLink = setContext(
  async (data:any) => {
    let accessToken: any = localStorage.getItem(Object.keys(localStorage).find((key) => key.includes('accessToken')) as string) || '';
    const saId:any = localStorage.getItem('saId')
    const virutalProgramId:string | null = localStorage.getItem('virtualProgramId')
    const studentClasscode: string |null = localStorage.getItem('studentClassCode')

    // If there is no normal access token, check if we should use temporary tokens
    // ONLY on the virtual-school payment-success page (before login flow)
    if (!accessToken && (window.location.pathname.includes("/virtual-school/payment-success") || window.location.pathname.includes("/payment-success"))) {
      const tempTokensStr = localStorage.getItem("vs_temp_tokens");
      if (tempTokensStr) {
        try {
          const tempTokens = JSON.parse(tempTokensStr);
          let tempAccessToken = tempTokens.AccessToken;
          
          const validation = await validateJwtToken(tempAccessToken);
          if (!validation?.isValid && tempTokens.RefreshToken) {
            console.log("Temporary token expired, refreshing via Cognito InitiateAuth...");
            const refreshed = await refreshCognitoTokens(tempTokens.RefreshToken);
            tempAccessToken = refreshed.AccessToken;
            
            // Store refreshed tokens
            localStorage.setItem("vs_temp_tokens", JSON.stringify({
              ...tempTokens,
              AccessToken: refreshed.AccessToken,
              IdToken: refreshed.IdToken
            }));
          }
          accessToken = tempAccessToken;
        } catch (err) {
          console.error("Error validating or refreshing temporary tokens:", err);
        }
      }
    } else if (accessToken) {
      localStorage.removeItem("vs_temp_tokens");
      const validation =  await validateJwtToken(accessToken);
      if (!validation?.isValid) {
        try {
          const session : any = await getNewAccessToken();
          const refreshedToken = session?.tokens?.accessToken?.toString();
          accessToken = refreshedToken || "";
        } catch (err) {
          console.log("Token refresh failed, skipping request");
          errorToast("Session Expired..")
          store.dispatch(authLogout()); 
          throw new Error("Session Expired..");
        }
      }
    }

    return _buildAuthHeader(accessToken,saId, virutalProgramId, studentClasscode);
  }
);


const _buildAuthHeader = (token: string,saId:string, virutalProgramId: string |null, studentClasscode : string | null ) => {
  return {
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
      saId:saId?saId:'',
      vprogramid : virutalProgramId ? virutalProgramId : '',
      classcode : studentClasscode ? studentClasscode : ''
    },
  };
};

const getCurrentUserDetail = async () => {
  return await getCurrentUser();
}

const logout = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      console.error("[GraphQL Error]:", err);
      const isAuthError = err?.message?.toLowerCase()?.includes("unauthorized") ||
                          err?.message?.toLowerCase()?.includes("user needs to be authenticated") ||
                          err?.message?.toLowerCase()?.includes("token expired");

      if (isAuthError) {
        errorToast(err?.message)
        store.dispatch(authLogout()); 
      }
    }
  }

  if (networkError) {
    console.error("[Network Error]:", networkError);
  }
});

const httpLink = new HttpLink({ uri: config.backend_url });
const client = new ApolloClient({
  link: from([logout, setAuthorizationLink, httpLink]),
  cache: new InMemoryCache({
    addTypename: false
  }),
  defaultOptions: {
    query: {
      fetchPolicy: FETCH_POLICY,
      errorPolicy: "all",
    },
  },
});

const postHogApiKey = process.env.REACT_APP_POSTHOG_KEY;
const postHogHost = process.env.REACT_APP_POSTHOG_HOST;

console.log("postHogApiKey, postHogHost", postHogApiKey, postHogHost);
if (postHogApiKey !== '' && postHogHost !== '' && postHogApiKey && postHogHost) {
  posthog.init(postHogApiKey, {
    api_host: postHogHost
  });
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <ApolloProvider client={client}>
    <PostHogProvider client={posthog}>
      <Provider store={store}>
          <GlobalProvider>
            <MeetingProvider>
              <ChimeMeetingProvider>
                <BrowserRouter>
                  <PersistGate loading={null} persistor={persistor}>
                    <App />
                  </PersistGate>
                </BrowserRouter>
              </ChimeMeetingProvider>
            </MeetingProvider>
          </GlobalProvider>
      </Provider>
    </PostHogProvider>
  </ApolloProvider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
